import { BankOutlined, CalendarOutlined, DollarOutlined, FileExcelOutlined, FilePdfOutlined, SyncOutlined } from '@ant-design/icons';
import { Button, Card, Col, ConfigProvider, DatePicker, Row, Space, Statistic, Table, Tag } from 'antd';
import frFR from "antd/locale/fr_FR";
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import Footer from '../../components/Footer';
import NavBar from '../../components/NavBar';
import SideBar from '../../components/SideBar';
import { NAVY, drawEtatHeaderFooter, finalizeEtatPdf, loadEntete, loadLogos } from '../stats/sinistre/etatsSinistresFunctions';
import axiosInstance from '../../variables/api/axiosInstance';
import { frenchDate } from '../../variables/functions';
import { displayNumbers } from '../../variables/numbersFunctions';

const VersementsBancairesVTSync = () => {
    const [versements, setVersements] = useState([]);
    const [totalMontant, setTotalMontant] = useState(0);
    const [totalTimbre, setTotalTimbre] = useState(0);
    const [loading, setLoading] = useState(false);
    const [moisSelectionne, setMoisSelectionne] = useState(null);

    const charger = (mois) => {
        setLoading(true);
        const params = mois ? { mois } : {};
        axiosInstance
            .get('/api/vt/versements-bancaires', { params })
            .then(res => {
                setVersements(res.data.versements || []);
                setTotalMontant(res.data.totalMontant || 0);
                setTotalTimbre(res.data.totalTimbre || 0);
            })
            .catch(() => {
                setVersements([]);
                setTotalMontant(0);
                setTotalTimbre(0);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        charger(null);
    }, []);

    // Export Excel du mois affiché (données déjà filtrées par l'endpoint via ?mois=YYYY-MM).
    const exporterExcel = () => {
        if (!versements.length) return;
        const lignes = versements.map((v, i) => ({
            "N°": i + 1,
            "Date versement": v.dateVersement ? dayjs(v.dateVersement).format('DD/MM/YYYY') : '',
            "Mois référence": v.moisReference ? dayjs(v.moisReference).format('MMMM YYYY') : '',
            "Montant versé (FCFA)": Math.trunc(v.montantVerse ?? 0),
            "Timbre (FCFA)": Math.trunc(v.timbre ?? 0),
            "Report veille (FCFA)": Math.trunc(v.reportVeille ?? 0),
            "Solde à reporter (FCFA)": Math.trunc(v.soldeAReporter ?? 0),
            "Source": v.sourceApp || 'STCA',
            "Organisme": v.codeOrganisme || '',
            "Agence": v.codeAgence || '',
            "Reçu le": v.receivedAt ? dayjs(v.receivedAt).format('DD/MM/YYYY HH:mm') : '',
        }));
        // Ligne de totaux
        lignes.push({
            "N°": '',
            "Date versement": '',
            "Mois référence": 'TOTAL',
            "Montant versé (FCFA)": Math.trunc(totalMontant),
            "Timbre (FCFA)": Math.trunc(totalTimbre),
            "Report veille (FCFA)": '',
            "Solde à reporter (FCFA)": '',
            "Source": '',
            "Organisme": '',
            "Agence": '',
            "Reçu le": '',
        });
        const ws = XLSX.utils.json_to_sheet(lignes);
        ws["!cols"] = [
            { wch: 5 }, { wch: 14 }, { wch: 16 }, { wch: 18 }, { wch: 14 },
            { wch: 18 }, { wch: 18 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 18 },
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Versements VT");
        const suffixe = moisSelectionne || dayjs().format('YYYY-MM-DD');
        XLSX.writeFile(wb, `Versements_VT_${suffixe}.xlsx`);
    };

    // Export PDF avec l'en-tête (logo Pool) et le pied de page (logos compagnies + pagination)
    // standard des documents PoolIns, via le gabarit partagé drawEtatHeaderFooter.
    const exporterPDF = async () => {
        if (!versements.length) return;
        // Format de nombre COMPATIBLE jsPDF : séparateur = ESPACE ASCII. Le format fr-FR de
        // displayNumbers utilise une espace fine insécable (U+202F) que jsPDF rend « / » et ne
        // sait pas couper → les montants s'affichaient « 2 / 7 4 4 / 9 » (repli caractère par
        // caractère). L'espace ASCII est rendue correctement ET reste une coupure normale.
        const pdfNum = (v) => new Intl.NumberFormat('en-DE').format(Math.trunc(v ?? 0)).replace(/\./g, ' ');
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const formattedNow = new Date().toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
        const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

        const headers = [
            'N°', 'Date versement', 'Mois réf.', 'Montant versé (FCFA)', 'Timbre (FCFA)',
            'Report veille (FCFA)', 'Solde à reporter (FCFA)', 'Source', 'Agence', 'Reçu le',
        ];
        const rows = versements.map((v, i) => ([
            i + 1,
            v.dateVersement ? frenchDate(v.dateVersement) : '',
            v.moisReference ? dayjs(v.moisReference).format('MMMM YYYY') : '',
            pdfNum(v.montantVerse),
            pdfNum(v.timbre),
            pdfNum(v.reportVeille),
            pdfNum(v.soldeAReporter),
            v.sourceApp || 'STCA',
            v.codeAgence ? (v.codeOrganisme ? `${v.codeOrganisme} / ${v.codeAgence}` : v.codeAgence) : '—',
            v.receivedAt ? dayjs(v.receivedAt).format('DD/MM/YYYY HH:mm') : '—',
        ]));
        rows.push([
            { content: `TOTAL (${versements.length})`, colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: pdfNum(totalMontant), styles: { halign: 'right', fontStyle: 'bold' } },
            { content: pdfNum(totalTimbre), styles: { halign: 'right', fontStyle: 'bold' } },
            '', '', '', '', '',
        ]);

        autoTable(doc, {
            head: [headers],
            body: rows,
            theme: 'grid',
            styles: { fontSize: 7, cellPadding: 1.3, lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45], overflow: 'linebreak', valign: 'middle' },
            headStyles: { fillColor: NAVY, textColor: 255, fontSize: 7, fontStyle: 'bold', lineWidth: 0.1, lineColor: NAVY },
            alternateRowStyles: { fillColor: [246, 247, 251] },
            margin: { top: 32, bottom: 22, left: 14, right: 14 },
            columnStyles: {
                0: { cellWidth: 12, halign: 'center' },
                1: { cellWidth: 26, halign: 'center' },
                2: { cellWidth: 28 },
                3: { cellWidth: 30, halign: 'right' },
                4: { cellWidth: 24, halign: 'right' },
                5: { cellWidth: 28, halign: 'right' },
                6: { cellWidth: 30, halign: 'right' },
                7: { cellWidth: 20, halign: 'center' },
                8: { cellWidth: 28, halign: 'center' },
                9: { cellWidth: 30, halign: 'center' },
            },
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: moisSelectionne
                    ? `VERSEMENTS BANCAIRES VT — ${dayjs(`${moisSelectionne}-01`).format('MMMM YYYY').toUpperCase()}`
                    : 'VERSEMENTS BANCAIRES VT (STCA / DTRF)',
                subtitle: `${versements.length} versement(s)`,
                enteteData, logos, formattedNow,
            }),
        });

        const suffixe = moisSelectionne || dayjs().format('YYYY-MM-DD');
        finalizeEtatPdf(doc);
        doc.save(`Versements_VT_${suffixe}.pdf`);
    };

    const colonnes = [
        {
            title: 'Date versement',
            dataIndex: 'dateVersement',
            key: 'dateVersement',
            render: v => frenchDate(v),
            sorter: (a, b) => (a.dateVersement || '').localeCompare(b.dateVersement || ''),
        },
        {
            title: 'Mois référence',
            dataIndex: 'moisReference',
            key: 'moisReference',
            render: v => v ? dayjs(v).format('MMMM YYYY') : '—',
        },
        {
            title: 'Montant versé (FCFA)',
            dataIndex: 'montantVerse',
            key: 'montantVerse',
            align: 'right',
            render: v => displayNumbers(v ?? 0),
            sorter: (a, b) => (a.montantVerse ?? 0) - (b.montantVerse ?? 0),
        },
        {
            title: 'Timbre (FCFA)',
            dataIndex: 'timbre',
            key: 'timbre',
            align: 'right',
            render: v => displayNumbers(v ?? 0),
        },
        {
            title: 'Report veille (FCFA)',
            dataIndex: 'reportVeille',
            key: 'reportVeille',
            align: 'right',
            render: v => displayNumbers(v ?? 0),
        },
        {
            title: 'Solde à reporter (FCFA)',
            dataIndex: 'soldeAReporter',
            key: 'soldeAReporter',
            align: 'right',
            render: v => displayNumbers(v ?? 0),
        },
        {
            title: 'Source',
            dataIndex: 'sourceApp',
            key: 'sourceApp',
            render: v => <Tag color="blue">{v || 'STCA'}</Tag>,
        },
        {
            title: 'Agence',
            key: 'agence',
            render: (_, r) => r.codeAgence
                ? <Tag color="geekblue">{r.codeOrganisme ? `${r.codeOrganisme} / ${r.codeAgence}` : r.codeAgence}</Tag>
                : '—',
            sorter: (a, b) => (a.codeAgence || '').localeCompare(b.codeAgence || ''),
        },
        {
            title: 'Reçu le',
            dataIndex: 'receivedAt',
            key: 'receivedAt',
            render: v => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—',
        },
    ];

    return (
        <ConfigProvider locale={frFR}>
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    <SideBar currentMenu={"CAISSE"} />

                    <div className="layout-page">
                        <NavBar />

                        <div className="content-wrapper">
                            <div className="container-xxl flex-grow-1 container-p-y">

                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h4 className="mb-0">
                                        <BankOutlined style={{ marginRight: 8 }} />
                                        Versements bancaires VT (STCA / DTRF)
                                    </h4>
                                    <Space>
                                        <DatePicker
                                            picker="month"
                                            placeholder="Filtrer par mois"
                                            onChange={date => {
                                                const mois = date ? date.format('YYYY-MM') : null;
                                                setMoisSelectionne(mois);
                                                charger(mois);
                                            }}
                                            format="MMMM YYYY"
                                            allowClear
                                        />
                                        <Button
                                            danger
                                            icon={<FilePdfOutlined />}
                                            onClick={exporterPDF}
                                            disabled={!versements.length}
                                        >
                                            Exporter PDF
                                        </Button>
                                        <Button
                                            type="primary"
                                            icon={<FileExcelOutlined />}
                                            onClick={exporterExcel}
                                            disabled={!versements.length}
                                        >
                                            Exporter Excel
                                        </Button>
                                    </Space>
                                </div>

                                <Row gutter={16} className="mb-4">
                                    <Col xs={24} sm={8}>
                                        <div className="card">
                                            <div className="card-body">
                                                <div className="d-flex align-items-start justify-content-between">
                                                    <div className="content-left">
                                                        <span className="text-heading">Versements reçus</span>
                                                        <div className="d-flex align-items-center my-1">
                                                            <h5 className="mb-0 me-2">{versements.length}</h5>
                                                        </div>
                                                        <small className="mb-0">enregistrements</small>
                                                    </div>
                                                    <div className="avatar">
                                                        <span className="avatar-initial rounded bg-label-primary">
                                                            <CalendarOutlined />
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <div className="card">
                                            <div className="card-body">
                                                <div className="d-flex align-items-start justify-content-between">
                                                    <div className="content-left">
                                                        <span className="text-heading">Total versé</span>
                                                        <div className="d-flex align-items-center my-1">
                                                            <h5 className="mb-0 me-2">{displayNumbers(Math.trunc(totalMontant))} FCFA</h5>
                                                        </div>
                                                        <small className="mb-0">cumul période</small>
                                                    </div>
                                                    <div className="avatar">
                                                        <span className="avatar-initial rounded bg-label-success">
                                                            <DollarOutlined />
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <div className="card">
                                            <div className="card-body">
                                                <div className="d-flex align-items-start justify-content-between">
                                                    <div className="content-left">
                                                        <span className="text-heading">Total timbre</span>
                                                        <div className="d-flex align-items-center my-1">
                                                            <h5 className="mb-0 me-2">{displayNumbers(Math.trunc(totalTimbre))} FCFA</h5>
                                                        </div>
                                                        <small className="mb-0">cumul période</small>
                                                    </div>
                                                    <div className="avatar">
                                                        <span className="avatar-initial rounded bg-label-info">
                                                            <SyncOutlined />
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="card">
                                    <div className="card-body">
                                        <Table
                                            dataSource={versements}
                                            columns={colonnes}
                                            rowKey="versementId"
                                            loading={loading}
                                            pagination={{ pageSize: 20, showSizeChanger: true }}
                                            scroll={{ x: 900 }}
                                            size="small"
                                            summary={() => versements.length > 0 && (
                                                <Table.Summary fixed>
                                                    <Table.Summary.Row style={{ fontWeight: 'bold', background: '#fafafa' }}>
                                                        <Table.Summary.Cell index={0} colSpan={2}>Total</Table.Summary.Cell>
                                                        <Table.Summary.Cell index={2} align="right">
                                                            {displayNumbers(Math.trunc(totalMontant))}
                                                        </Table.Summary.Cell>
                                                        <Table.Summary.Cell index={3} align="right">
                                                            {displayNumbers(Math.trunc(totalTimbre))}
                                                        </Table.Summary.Cell>
                                                        <Table.Summary.Cell index={4} colSpan={5} />
                                                    </Table.Summary.Row>
                                                </Table.Summary>
                                            )}
                                        />
                                    </div>
                                </div>

                            </div>
                            <Footer />
                        </div>
                    </div>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default VersementsBancairesVTSync;
